import { useCallback, useEffect } from 'react';

import { Form } from 'antd';

import { useDescriptionValidation } from '@/shared/hooks';
import { FORM_LABELS, FORM_PLACEHOLDERS, MODAL_WIDTHS, VALIDATION_RULES } from '@/shared/lib/constants';
import { validateDescriptionLength } from '@/shared/lib/text';
import { BaseModal } from '@/shared/ui/BaseModal';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';

type FormValues = {
  description: string;
};

type DescriptionEditModalProps = {
  currentDescription?: string | null;
  open: boolean;
  isLoading?: boolean;
  width?: number;
  minLength?: number;
  maxLength?: number;
  errorMessage?: string;
  onClose: () => void;
  onSubmit: (description: string) => Promise<void> | void;
};

const TITLE = '설명 수정';
const OK_TEXT = '저장';
const DEFAULT_ERROR_MESSAGE = '설명 수정 실패';

export const DescriptionEditModal = ({
  open,
  currentDescription,
  onClose,
  onSubmit,
  isLoading = false,
  width = MODAL_WIDTHS.COMPACT_FORM,
  minLength = 0,
  maxLength = VALIDATION_RULES.ASSET_DESCRIPTION_MAX_LENGTH,
  errorMessage = DEFAULT_ERROR_MESSAGE,
}: DescriptionEditModalProps) => {
  const [form] = Form.useForm<FormValues>();

  const watchedDescription = Form.useWatch('description', form);
  const initialDescription = currentDescription ?? '';
  const currentDescriptionValue = watchedDescription ?? '';

  const { descriptionError, handleValuesChange, validateBeforeSubmit, resetError } = useDescriptionValidation({
    minLength,
    maxLength,
    errorMessage,
  });
  const isDescriptionLengthValid = validateDescriptionLength(currentDescriptionValue, minLength, maxLength);
  const isSubmitDisabled =
    currentDescriptionValue === initialDescription || !!descriptionError || !isDescriptionLengthValid;

  const handleClose = useCallback(() => {
    form.resetFields();
    resetError();
    onClose();
  }, [form, resetError, onClose]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      const description = values.description ?? '';

      if (!validateBeforeSubmit(description)) {
        return;
      }

      await onSubmit(description);
      handleClose();
    },
    [validateBeforeSubmit, onSubmit, handleClose]
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        description: initialDescription,
      });
      resetError();
    }
  }, [open, form, initialDescription, resetError]);

  return (
    <BaseModal
      title={TITLE}
      okText={OK_TEXT}
      width={width}
      open={open}
      confirmLoading={isLoading}
      onClose={handleClose}
      onOk={() => form.submit()}
      okButtonProps={{ disabled: isSubmitDisabled }}>
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange} onFinish={handleSubmit}>
        <Form.Item name="description" label={FORM_LABELS.DESCRIPTION} validateTrigger={['onSubmit']}>
          <DescriptionTextArea
            placeholder={FORM_PLACEHOLDERS.DESCRIPTION}
            status={descriptionError ? 'error' : undefined}
            minLength={minLength}
            maxLength={maxLength}
            showHelper
          />
        </Form.Item>
      </Form>
    </BaseModal>
  );
};
